import { useEffect, useState } from "react";
import api from "@/utils/api";
import { CreditCard, Calendar, AlertCircle, CheckCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { getToken, getUser } from "@/utils/auth";
import { useSettings } from "@/context/SettingsContext";

const MemberSubscriptions = () => {
  const { formatCurrency, formatDate } = useSettings();
  const user = getUser();
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState([]);

  useEffect(() => {
    fetchMemberData();
  }, []);

  const fetchMemberData = async () => {
    try {
      const response = await api.get('/members/me');
      
      const member = response.data.member || (response.data.id ? response.data : null);
      const resUser = response.data.user || (response.data.member_id ? null : response.data);
      
      if (!member) {
        try {
          const membersResp = await api.get('/members');
          const u = resUser || user;
          const candidate = membersResp.data.find((m) => 
            (u?.member_id && m.id === u.member_id) ||
            (u?.email && (m.email || '').toLowerCase() === (u.email || '').toLowerCase())
          );
          
          if (candidate) {
            const detailResp = await api.get(`/members/${candidate.id}`);
            setSubscriptions(detailResp.data.subscriptions || []);
          } else {
            setSubscriptions([]);
          }
        } catch (e) {
          setSubscriptions([]);
        }
      } else {
        setSubscriptions(response.data.subscriptions || []);
      }
      setLoading(false);
    } catch (error) {
      console.error("Failed to load subscriptions:", error);
      const detail = error.response?.data?.detail || "";
      if (error.response?.status === 404 && detail.toLowerCase().includes("member not found")) {
        try {
          const membersResp = await api.get('/members');
          const u = user;
          const candidate = membersResp.data.find((m) => 
            (u?.member_id && m.id === u.member_id) ||
            (u?.email && (m.email || '').toLowerCase() === (u.email || '').toLowerCase())
          );
          
          if (candidate) {
            const detailResp = await api.get(`/members/${candidate.id}`);
            setSubscriptions(detailResp.data.subscriptions || []);
          } else {
            setSubscriptions([]);
          }
        } catch (e) {
          setSubscriptions([]);
        }
        setLoading(false);
      } else {
        toast.error("Failed to load subscriptions");
        setLoading(false);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'bg-lime-100 text-lime-800 border-lime-200';
      case 'expired': return 'bg-zinc-100 text-zinc-800 border-zinc-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-zinc-100 text-zinc-800 border-zinc-200';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-orange-100 text-orange-700';
      case 'partial': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-zinc-100 text-zinc-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lime-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            My Subscriptions
          </h1>
          <p className="text-zinc-500 mt-1">Manage your membership plans and history</p>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        {subscriptions.length === 0 ? (
          <div className="bg-white border border-zinc-200 rounded-xl p-8 text-center text-zinc-500">
            No subscription history found
          </div>
        ) : (
          subscriptions.map((sub) => (
            <div key={sub.id} className="bg-white border border-zinc-200 rounded-xl p-4 shadow-sm">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-lime-100 rounded-lg text-lime-700">
                    <CreditCard size={20} />
                  </div>
                  <div>
                    <div className="font-medium text-zinc-900">{sub.plan?.name || "Membership"}</div>
                    <div className="text-xs text-zinc-500">ID: {sub.id.slice(0, 8)}</div>
                  </div>
                </div>
                <Badge variant="outline" className={getStatusColor(sub.status)}>
                  {sub.status}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm border-t border-zinc-100 pt-4">
                <div>
                  <span className="text-zinc-500 block text-xs mb-1">Duration</span>
                  <div className="flex flex-col gap-0.5">
                    <span>{formatDate(sub.start_date)}</span>
                    <span className="text-zinc-400 text-xs">to</span>
                    <span>{formatDate(sub.end_date)}</span>
                  </div>
                </div>
                <div>
                  <span className="text-zinc-500 block text-xs mb-1">Payment Details</span>
                  <div className="font-medium">
                    {formatCurrency(sub.amount_paid + sub.amount_due)}
                  </div>
                  {sub.amount_due > 0 && (
                    <div className="text-xs text-orange-600 font-medium">
                      Due: {formatCurrency(sub.amount_due)}
                    </div>
                  )}
                  <Badge variant="secondary" className={`mt-2 ${getPaymentStatusColor(sub.payment_status)}`}>
                    {sub.payment_status}
                  </Badge>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white border border-zinc-200 rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Plan</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Duration</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Payment</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {subscriptions.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-zinc-500">
                    No subscription history found
                  </td>
                </tr>
              ) : (
                subscriptions.map((sub) => (
                  <tr key={sub.id} className="hover:bg-zinc-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-lime-100 rounded-lg text-lime-700">
                          <CreditCard size={20} />
                        </div>
                        <div>
                          <div className="font-medium text-zinc-900">{sub.plan?.name || "Membership"}</div>
                          <div className="text-xs text-zinc-500">ID: {sub.id.slice(0, 8)}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant="outline" className={getStatusColor(sub.status)}>
                        {sub.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1 text-sm text-zinc-600">
                        <div className="flex items-center gap-2">
                          <span className="text-zinc-400">Start:</span>
                          <span>{formatDate(sub.start_date)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-zinc-400">End:</span>
                          <span>{formatDate(sub.end_date)}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-zinc-900">
                        {formatCurrency(sub.amount_paid + sub.amount_due)}
                      </div>
                      {sub.amount_due > 0 && (
                        <div className="text-xs text-orange-600 mt-1">
                          Due: {formatCurrency(sub.amount_due)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant="secondary" className={getPaymentStatusColor(sub.payment_status)}>
                        {sub.payment_status}
                      </Badge>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MemberSubscriptions;
